import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getCookie } from './auth/cookies';
import Leaderboard from './leaderboard';
import HackathonCountdown from './countdown';
import axios from 'axios';
import customRenderers from './customMD';
import ReactMarkdown from 'react-markdown';

export default function Home() {
  const [isLogged, setIsLogged] = useState(false);
  const [isStaff, setIsStaff] = useState(false);
  const [hasTrainerAccess, setHasTrainerAccess] = useState(false);
  const [mainContent, setMainContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [readmeContent, setReadmeContent] = useState("");
  const readmeRenderers = {
  h1: ({ children }) => (
    <h1 className="text-3xl font-bold mt-8 mb-5 text-secondary-dark dark:text-white">
      {children}
    </h1>
  ),

  h2: ({ children }) => (
    <h2 className="text-2xl font-bold mt-8 mb-4 text-secondary-dark dark:text-white">
      {children}
    </h2>
  ),

  h3: ({ children }) => (
    <h3 className="text-xl font-semibold mt-6 mb-3 text-secondary-dark dark:text-white">
      {children}
    </h3>
  ),

  p: ({ children }) => (
    <p className="text-lg mb-4 leading-relaxed text-secondary dark:text-gray-300">
      {children}
    </p>
  ),

  ul: ({ children }) => (
    <ul className="list-disc pl-8 mb-5 space-y-2 text-secondary dark:text-gray-300">
      {children}
    </ul>
  ),

  ol: ({ children }) => (
    <ol className="list-decimal pl-8 mb-5 space-y-2 text-secondary dark:text-gray-300">
      {children}
    </ol>
  ),

  li: ({ children }) => (
    <li className="text-lg leading-relaxed">
      {children}
    </li>
  ),

  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline font-medium hover:opacity-80"
    >
      {children}
    </a>
  ),

  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-secondary pl-4 my-6 italic text-secondary dark:text-gray-300">
      {children}
    </blockquote>
  ),

  code: ({ children }) => (
    <code className="font-mono bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded">
      {children}
    </code>
  ),
};  

  const checkIfStaff = () => {
    const sessionid = getCookie('sessionid');
    if (sessionid) {
      axios.get('/api/check_if_staff/', {
        headers: { 'X-CSRFToken': getCookie('csrftoken') }
      })
      .then((res) => {
        if (res.data.is_staff) {
          setIsStaff(true);
        }
      })
      .catch((err) => {
        console.error("Error checking staff status:", err);
      });
    }
  }

  useEffect(() => {
    // Load main content
    axios.get('api/load_main_content/')
      .then((res) => {
        setMainContent(res.data.content);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading main content:", err);
        setLoading(false);
      });

    // Check for trainer access
    axios.get('/api/has_trainer_access/', {
      headers: { 'X-CSRFToken': getCookie('csrftoken') }
    })
      .then((res) => {
        setHasTrainerAccess(true);
      })
      .catch((err) => {
        console.error("Error checking trainer access:", err);
      });

    // Check login status
    const sessionid = getCookie('csrftoken');
    if (sessionid) {
      setIsLogged(true);
      checkIfStaff();
    }
  }, []);
  useEffect(() => {
   fetch("/readme.md")
    .then((response) => {
      if (!response.ok) {
        throw new Error("Could not load README");
      }
      return response.text();
    })
    .then((text) => {
      setReadmeContent(text);
    })
    .catch((error) => {
      console.error("Error loading README:", error);
    });
}, []);
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-primary-light dark:bg-primary">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="bg-primary-light dark:bg-primary min-h-screen text-secondary dark:text-gray-200 font-sans">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        {/* Hero Section */}
        <header className="py-12 md:py-20">
          <div className="text-center md:text-left">
            <h1 className="text-2xl md:text-4xl lg:text-4xl font-bold mb-6 text-secondary-dark dark:text-white leading-tight">
              {mainContent?.bigtitle || "Hackathon Platform"}
            </h1>
            <p className="text-lg md:text-xl mb-8 text-secondary dark:text-gray-300 max-w-2xl">
              {mainContent?.subtitle || "Join our premier coding event and showcase your skills"}
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center md:justify-start">
              {isLogged ? (
                <>
                  {isStaff && (
                    <Link to="/adminpage">
                      <button className="bg-secondary hover:bg-secondary-dark text-white font-medium px-6 py-3 rounded-md transition duration-300 shadow-md flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Admin Dashboard
                      </button>
                    </Link>
                  )}

                  <Link to="/teampage">
                    <button className="bg-secondary hover:bg-secondary-dark text-white font-medium px-6 py-3 rounded-md transition duration-300 shadow-md flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Your Team
                    </button>
                  </Link>

                  <Link to="/submission">
                    <button className="bg-secondary hover:bg-secondary-dark text-white font-medium px-6 py-3 rounded-md transition duration-300 shadow-md flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      Submit Result
                    </button>
                  </Link>

                  {hasTrainerAccess && (
                    <Link to="/trainer">
                      <button className="bg-secondary hover:bg-secondary-dark text-white font-medium px-6 py-3 rounded-md transition duration-300 shadow-md flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        Trainer Access
                      </button>
                    </Link>
                  )}

                  <Link to="/logout">
                    <button className="bg-gray-600 hover:bg-gray-700 text-white font-medium px-6 py-3 rounded-md transition duration-300 shadow-md flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Logout
                    </button>
                  </Link>
                </>
              ) : (
                <Link to="/login">
                  <button className="bg-secondary hover:bg-secondary-dark text-white font-medium px-8 py-3 rounded-md transition duration-300 shadow-md flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                    Login
                  </button>
                </Link>
              )}
            </div>
          </div>
        </header>

        {isLogged && (
          <div className="mb-12">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 border-l-4 border-secondary">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-lg font-medium dark:text-white">
                    Access challenge files and details
                  </p>
                  <div className="mt-3">
                    <Link to="/challengedata">
                      <button className="bg-secondary hover:bg-secondary-dark text-white font-medium px-6 py-2 rounded-md transition duration-300 shadow-sm flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        View Challenge Files
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hackathon Description Section */}
        <section className="py-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-secondary-dark dark:text-white">
                {mainContent?.title || "About This Hackathon"}
              </h2>
              <div className="text-lg space-y-4 text-secondary dark:text-gray-300">
 		 <ReactMarkdown>
   		 {mainContent?.text || "Join us for an exciting coding challenge where you can showcase your skills and creativity."}
  		</ReactMarkdown>
		</div>
	</div>
            <div className="order-1 md:order-2 flex justify-center">
              {mainContent?.image ? (
                <img 
                  src={mainContent.image} 
                  alt="Hackathon" 
                  className="max-w-full h-auto rounded-lg shadow-xl object-cover"
                />
              ) : (
                <div className="bg-gray-300 dark:bg-gray-700 rounded-lg w-full h-64 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-12">
            <ReactMarkdown key="0" components={customRenderers}>{mainContent.middle}</ReactMarkdown>
        </section>
	
	{/* Challenge README Section */}
	<section className="py-12">
  	 <div className="max-w-5xl mx-auto">
    	  <ReactMarkdown components={readmeRenderers}>
	   {readmeContent}
    	  </ReactMarkdown>
  	 </div>
	</section>

	 {/* Countdown Section */}
        <div className="py-8">
          <HackathonCountdown />
        </div>

        {/* Leaderboard Section */}
        <section className="py-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-6 text-secondary-dark dark:text-white"></h2>
            <Leaderboard />
          </div>
        </section>

    
      </div>
    </div>
  );
}
